/**
 * CASL Ability Context and Provider
 *
 * Provides CASL ability instance throughout the application via React Context.
 * Automatically fetches user permissions and builds ability on mount.
 */

'use client'

import React, { createContext, useContext, useMemo } from 'react'
import { createContextualCan } from '@casl/react'
import { AppAbility, createDefaultAbility, createFullAccessAbility, defineAbilityFor } from './ability'
import { Spinner } from '@workspace/ui/components/Spinner'
import { authStore } from '@/shared/store/auth.store'
import { ADMIN_ROLE_NAME } from '@/shared/constants'
import { useSelector } from '@xstate/store/react'

/**
 * Ability Context
 */
const AbilityContext = createContext<AppAbility>(createDefaultAbility())

/**
 * Contextual Can component
 * Use this for declarative permission checks in JSX
 *
 * @example
 * ```tsx
 * <Can I="create" a="User">
 *   <Button>Create User</Button>
 * </Can>
 * ```
 */
export const Can = createContextualCan(AbilityContext.Consumer)

/**
 * Hook to access the ability instance
 *
 * @example
 * ```typescript
 * const ability = useAbility()
 * if (ability.can('create', 'User')) {
 *   // Show create button
 * }
 * ```
 */
export function useAbility() {
    return useContext(AbilityContext)
}

/**
 * Props for AbilityProvider
 */
interface AbilityProviderProps {
    children: React.ReactNode
    /**
     * Optional loading component to show while permissions are being fetched
     * If not provided, children will be rendered with default (no permissions) ability
     */
    loadingComponent?: React.ReactNode
    /**
     * Whether to show loading component while fetching permissions
     * Default: false (render children immediately with default ability)
     */
    showLoadingState?: boolean
}

/**
 * Ability Provider Component
 *
 * Fetches user permissions and provides CASL ability to children.
 * Place this high in your component tree, typically in Providers component.
 *
 * @example
 * ```tsx
 * <AbilityProvider>
 *   <App />
 * </AbilityProvider>
 * ```
 */
export function AbilityProvider({ children, loadingComponent, showLoadingState = false }: AbilityProviderProps) {
    const user = useSelector(authStore, state => state.context.user)

    // Build ability from permissions
    const ability = useMemo(() => {
        if (user && user.role && user.role.name === ADMIN_ROLE_NAME) {
            return createFullAccessAbility()
        }

        if (user && user.permissions) {
            return defineAbilityFor(user.permissions)
        }

        if (user?.role?.permissions) {
            return defineAbilityFor(user.role.permissions)
        }

        return createDefaultAbility()
    }, [user])

    // Show loading state if enabled
    if (showLoadingState) {
        return (
            <div className="flex h-screen w-screen items-center justify-center">
                {loadingComponent || <Spinner fontSize="lg" />}
            </div>
        )
    }

    return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
}
